import * as React from 'react'
import {
  ConfirmationModal,
  Box,
  Typography,
  FileUpload,
  Loader,
  Button,
  Tooltip,
  Grid,
  FormControl,
  InputLabel,
  Input,
} from 'cs-kevlar-react'
import { FormikTextField } from 'components'
import { Formik, FormikProps } from 'formik'
import {
  makeStyles,
  createStyles,
  Theme,
} from 'cs-kevlar-react/lib/es/theming/theme'
import { useConnectorApi, useAuth } from 'hooks'
import { InfoOutlined } from 'cs-kevlar-react/lib/es/icons'
import clsx from 'clsx'

type CertValues = {
  oldPassphrase: string
  newPassphrase: string
}

interface RenewCertificateProps {
  formikProps: FormikProps<CertValues>
  certData: string | null
  onSelectFile: (file: File | null) => void
  error: string | null
  onClose: () => void
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    formItem: {
      marginTop: theme.spacing(8),
    },
    textMargin: {
      marginTop: theme.spacing(4),
    },
    error: {
      wordWrap: 'break-word',
    },
    tooltip: {
      marginLeft: theme.spacing(4),
    },
    label: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
    },
    buttonBar: {
      display: 'flex',
      flexDirection: 'row-reverse',
    },
  }),
)

const DEFAULT_VALUES = {
  oldPassphrase: '',
  newPassphrase: '',
}

const RenewCertificate: React.FunctionComponent<RenewCertificateProps> = ({
  formikProps,
  certData,
  onSelectFile,
  error,
  onClose,
}) => {
  const classes = useStyles({})

  const downloadCert = () => {
    const download = document.createElement('a')
    download.target = '_blank'
    download.href = `data:application/x-pkcs12;base64,${certData}`
    download.download = 'certificate.p12'
    download.click()

    onClose()
  }

  return (
    <Grid container>
      {!certData && (
        <React.Fragment>
          {!formikProps.isSubmitting && (
            <React.Fragment>
              <Grid item xs={12}>
                <Typography>
                  Select the existing p12 certificate that the connector is
                  configured to use in appsettings.
                </Typography>
              </Grid>
              <Grid item xs={12} className={classes.formItem}>
                <FileUpload
                  id="file"
                  onFileSelected={onSelectFile}
                  showSelectedFileName
                >
                  Choose File
                </FileUpload>
              </Grid>
              <Grid item xs={12} className={classes.formItem}>
                <FormControl>
                  <InputLabel
                    className={classes.label}
                    htmlFor="oldPassphrase"
                    shrink
                  >
                    <Typography display="inline">
                      Passphrase for existing p12 file
                    </Typography>
                    <Tooltip
                      placement="top"
                      title="Can be found in appsettings for the connector."
                      aria-label="Can be found in appsettings for the connector."
                      arrow
                      className={classes.tooltip}
                    >
                      <InfoOutlined />
                    </Tooltip>
                  </InputLabel>
                  <Input
                    id="oldPassphrase"
                    value={formikProps.values.oldPassphrase}
                    onChange={formikProps.handleChange}
                    onBlur={formikProps.handleBlur}
                    fullWidth
                    type="password"
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} className={classes.formItem}>
                <FormControl>
                  <InputLabel
                    className={classes.label}
                    htmlFor="newPassphrase"
                    shrink
                  >
                    <Typography display="inline">
                      Passphrase for new p12 file
                    </Typography>
                    <Tooltip
                      placement="top"
                      title="This will need to be added to appsettings once the new certificate is used."
                      aria-label="This will need to be added to appsettings once the new certificate is used."
                      arrow
                      className={classes.tooltip}
                    >
                      <InfoOutlined />
                    </Tooltip>
                  </InputLabel>
                  <Input
                    id="newPassphrase"
                    value={formikProps.values.newPassphrase}
                    onChange={formikProps.handleChange}
                    onBlur={formikProps.handleBlur}
                    fullWidth
                    type="password"
                  />
                </FormControl>
              </Grid>
            </React.Fragment>
          )}

          {formikProps.isSubmitting && (
            <Grid item xs={12}>
              <Loader size="sm" text="Generating New Certificate" />
            </Grid>
          )}

          {error !== null && (
            <Grid item xs={12}>
              <Typography className={classes.error} color="error">
                {error}
              </Typography>
            </Grid>
          )}
        </React.Fragment>
      )}
      {certData && (
        <Box>
          <Typography variant="h5">Certificate Successfully Renewed</Typography>
          <Typography className={classes.textMargin}>
            Please download the certificate and update the connector's settings
            with the same passphrase that was supplied when renewing the
            certificate.
          </Typography>
          <Typography className={classes.textMargin}>
            This certificate is not stored in the cloud and if you choose to not
            download it right now then you will have to generate a new
            certificate later.
          </Typography>
          <Box className={clsx(classes.textMargin, classes.buttonBar)}>
            <Button color="primary" onClick={downloadCert}>
              Download
            </Button>
          </Box>
        </Box>
      )}
    </Grid>
  )
}

interface RenewCertificateModalProps {
  onCancel: () => void
  open: boolean
  customerAgencyId: string
  customerInterfaceId: string
}

export const RenewCertificateModal: React.FunctionComponent<RenewCertificateModalProps> = ({
  onCancel,
  open,
  customerAgencyId,
  customerInterfaceId,
}) => {
  const { createCertificateFromCsr } = useConnectorApi()
  const { user } = useAuth()
  const [file, setFile] = React.useState<File | null>(null)
  const [error, setError] = React.useState<string | null>(null)
  const [newCertData, setNewCertData] = React.useState<string | null>(null)
  const [certHelpers, setCertHelpers] = React.useState<any | null>(null)

  if (user === null) return null

  // lazy load the cert ehlpers only when the renew certificate modal is open
  React.useEffect(() => {
    if (!open || certHelpers !== null) return

    const loader = async () => {
      const loadedCertHelpers = await import(
        /* webpackChunkName: "certificateHelpers" */ 'utils/certificates/helpers'
      )
      setCertHelpers(loadedCertHelpers)
    }

    loader()
  }, [open, certHelpers])

  const onSubmit = (values: CertValues, actions: any) => {
    if (file === null) {
      actions.setSubmitting(false)
      return
    }

    setError(null)

    const callback = (msg: string | null, data?: string) => {
      if (msg) {
        setError(msg)
      }

      if (data) {
        setNewCertData(data)
      }

      actions.setSubmitting(false)
    }

    const reader = new FileReader()
    reader.onload = evt => {
      if (evt.target === null || certHelpers === null) return
      certHelpers.processP12(
        createCertificateFromCsr,
        evt.target.result as ArrayBuffer,
        values.oldPassphrase,
        values.newPassphrase,
        customerAgencyId,
        customerInterfaceId,
        user.name,
        callback,
      )
    }
    reader.onerror = () => {
      setError('There was an error reading the p12 file')
    }
    reader.readAsArrayBuffer(file)
  }

  const selectFile = (file: File | null) => {
    setFile(file)
  }

  const onClose = () => {
    setNewCertData(null)
    onCancel()
  }

  return (
    <Formik initialValues={DEFAULT_VALUES} onSubmit={onSubmit}>
      {formikProps => (
        <ConfirmationModal
          onCancel={onCancel}
          open={open}
          title="Manual Certificate Renewal"
          confirmText="Generate New Certificate"
          hideActions={newCertData !== null}
          onConfirm={formikProps.handleSubmit}
          isConfirmDisabled={certHelpers !== null && formikProps.isSubmitting}
          message={
            <RenewCertificate
              formikProps={formikProps}
              onSelectFile={selectFile}
              error={error}
              certData={newCertData}
              onClose={onClose}
            />
          }
        />
      )}
    </Formik>
  )
}
